import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { EnumModuloDTONecessitaFirma } from '@framework/api-client/api/inquiries/schemas/enumModuloDTONecessitaFirma.ts';
import { RichiestaModuloDTOPermessiItem } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTOPermessiItem.ts';
import { type DatiSessioneUtente } from '@framework/api-client/api/users/schemas/datiSessioneUtente.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import { getSubmissionDelegationRole } from '@framework/frontoffice-submission/module/delegation/DelegationMachine.ts';
import {
  type IModuleStateMachineRecord,
  ModuleStateMachine,
} from '@framework/frontoffice-submission/module/fsm/ModuleStateMachine.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { Divider, Flex, Space, Typography } from 'antd';
import clx from 'classnames';
import { lighten } from 'polished';
import type React from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { ActorPermissionIcons } from '../../../../routes/submission/delegation/components/ActorPermissionIcons.tsx';
import { ActorRoleTag } from '../../../../routes/submission/delegation/components/ActorRoleTag.tsx';

const { Text } = Typography;

const BannerWrapper = styled.div`
  padding: 16px;
  padding-left: 24px;
  border-radius: ${props => props.theme.borderRadius}px;
  background-color: ${props => lighten(0.45, props.theme.moduleDelegateColor)};
  border: 1px solid ${props => props.theme.moduleDelegateColor};

  margin-bottom: 8px;
  .ant-typography {
    font-size: 16px;
  }

  .banner-message .anticon {
    font-size: 32px;
    color: ${props => props.theme.moduleDelegateColor};
  }

  &.note {
    background-color: ${props => props.theme.colorInfoBg};
    border: 1px solid ${props => props.theme.colorInfoBorder};
    .banner-message .anticon {
      color: ${props => props.theme.colorInfo};
    }
  }

  &.warning {
    background-color: ${props => props.theme.colorWarningBg};
    border: 1px solid ${props => props.theme.colorWarningBorder};
    .banner-message .anticon {
      color: ${props => props.theme.colorWarning};
    }
  }
`;

export interface IModuleStateTopBoxProps {
  data: IModuleStateMachineRecord;
}

function isOwnerInCurrentStep(
  data: IModuleStateMachineRecord,
  user: DatiSessioneUtente | null,
) {
  return ModuleStateMachine.transitionsFor(data, user).length > 0;
}

function hasUserAllSubmissionModulePermissions(
  data: IModuleStateMachineRecord,
  user: DatiSessioneUtente | null,
) {
  const hasCurrentUserAllPermissions =
    data.module?.necessitaFirma == EnumModuloDTONecessitaFirma.NO
      ? data.module?.permessi?.includes(
          RichiestaModuloDTOPermessiItem.COMPILAZIONE,
        )
      : data.module?.permessi?.includes(
          RichiestaModuloDTOPermessiItem.COMPILAZIONE,
        ) &&
        data.module?.permessi?.includes(RichiestaModuloDTOPermessiItem.FIRMA);

  return hasCurrentUserAllPermissions;
}

interface IStateMessage {
  state: 'warning' | 'info' | 'none';
  message: React.ReactNode;
}

function getStateMessage(
  data: IModuleStateMachineRecord,
  user: DatiSessioneUtente | null,
): IStateMessage {
  if (hasUserAllSubmissionModulePermissions(data, user)) {
    return {
      state: 'none',
      message: null,
    };
  }

  if (data.module?.stato === SubmissionModuleState.Completed) {
    return {
      state: 'info',
      message: t(
        'submission-module.modulo-completato',
        'Il modulo è completato.',
      ),
    };
  }

  if (!isOwnerInCurrentStep(data, user)) {
    return {
      state: 'warning',
      message: t(
        'submission-module.stai-visualizzando-in-sola-lettura-il-modulo',
        'Stai visualizzando in sola lettura questo modulo.',
      ),
    };
  }

  switch (data.module?.stato) {
    case SubmissionModuleState.Compilation:
      return {
        state: 'info',
        message: t(
          'submission-module.necessario-compilare-il-modulo',
          'È necessario compilare il modulo.',
        ),
      };
    case SubmissionModuleState.Sign:
      return {
        state: 'info',
        message: t(
          'submission-module.necessario-firmare-il-modulo',
          'È necessario firmare il modulo.',
        ),
      };
  }

  return { state: 'none', message: null };
}

export function ModuleStateTopBox(props: IModuleStateTopBoxProps) {
  const { data } = props;
  const isMobile = useBreakpoint(down('md'));

  const currentUser = useCurrentUser();

  const role = getSubmissionDelegationRole(data.delegationData, currentUser);

  if (SubmissionLogic.isSubmissionSent(data.submission)) {
    return <Divider style={{ margin: 0 }} />;
  }

  const { state, message } = getStateMessage(data, currentUser);
  const isSignable = ModuleStateMachine.can(
    data,
    '.signable',
    currentUser,
  ).allowed;

  if (state === 'none') return <Divider style={{ margin: 0 }} />;

  const actorItem = (
    <Space size={6}>
      <ActorRoleTag role={role} />
      <ActorPermissionIcons
        permission={data.module?.permessi}
        isSignable={isSignable}
      />
    </Space>
  );

  return (
    <BannerWrapper
      className={clx({
        warning: state === 'warning',
        info: state === 'info',
      })}
    >
      <Flex vertical gap={12}>
        {isMobile && <AlignRow justify="flex-end">{actorItem}</AlignRow>}
        <FlexRow right={!isMobile ? actorItem : undefined}>
          <Space className="banner-message" size={12}>
            {state === 'warning' && <WarningOutlined />}
            {state === 'info' && <InfoCircleOutlined />}
            <Text>{message}</Text>
          </Space>
        </FlexRow>
      </Flex>
    </BannerWrapper>
  );
}
